// React Query wrappers around TokenUsageClient. staleTime/refetchInterval
// are tuned so the UI never polls faster than the source data actually
// refreshes: the live current-month document is regenerated server-side on
// a ~15 min cadence (per PR3), so a 5 min client refetch interval is more
// than enough to feel "live" without hammering the API. Historical archives
// are immutable once isFinal === true, so they get a long staleTime and no
// refetchInterval at all.
import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMsalAuth } from './useMsalAuth';
import { createTokenUsageClientFor } from '../services/tokenusage.factory';
import { NotFoundError } from '../utils/errors';
import type { AvailablePeriodsResult, TokenUsageDocument } from '../types/tokenusage';

const FIVE_MINUTES = 5 * 60 * 1000;
const TWO_MINUTES = 2 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

/** Don't retry 404s — they mean "no archive for this period/user", not a transient failure. */
function retryUnlessNotFound(failureCount: number, error: unknown): boolean {
  if (error instanceof NotFoundError) return false;
  return failureCount < 2;
}

// createTokenUsageClientFor is async (see tokenusage.factory.ts, ADO thread
// 975046): the fixture module is loaded via a dynamic import gated on
// import.meta.env.DEV so it's dead-code-eliminated out of production
// builds. The memoized value is therefore a Promise, which each queryFn
// below awaits before issuing its call.
//
// accountKey is a stable identity for the signed-in MSAL account, included
// in every CALLER-SCOPED query key below (current month, historical period,
// available periods). The QueryClient in main.tsx is a module-level
// singleton shared across the whole app lifetime; if the active MSAL
// account changes WITHOUT destroying that provider (e.g. a multi-account
// browser session switching accounts), React Query would otherwise keep
// serving the previous account's cached "my usage" data to the new account
// until the next refetch -- a cross-account data disclosure (ADO thread
// 975053). Admin queries (useAdminUserUsage/useAdminAllUsers) already key on
// an explicit target oid or are global, caller-independent data, so they
// don't need this.
function useTokenUsageClient() {
  const { getToken, account } = useMsalAuth();
  const client = useMemo(() => createTokenUsageClientFor(getToken), [getToken]);
  const accountKey = account?.homeAccountId ?? account?.localAccountId ?? 'anonymous';
  return { client, accountKey };
}

/** The caller's own current (in-progress) month. Polls ~every 5 min. */
export function useCurrentUsage(): UseQueryResult<TokenUsageDocument, Error> {
  const { client, accountKey } = useTokenUsageClient();
  return useQuery<TokenUsageDocument, Error>({
    queryKey: ['tokenusage', 'current', accountKey],
    queryFn: async () => (await client).getCurrentMonth(),
    staleTime: TWO_MINUTES,
    refetchInterval: FIVE_MINUTES,
    retry: retryUnlessNotFound,
  });
}

/**
 * A specific historical (or current) period for the caller's own OID.
 * `enabled` lets callers defer the request until a period is actually
 * selected. Historical archives don't change once written, so staleTime is
 * long and there's no refetchInterval.
 */
export function useHistoricalUsage(
  year: number | undefined,
  month: number | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<TokenUsageDocument, Error> {
  const { client, accountKey } = useTokenUsageClient();
  const enabled = (options?.enabled ?? true) && year != null && month != null;
  return useQuery<TokenUsageDocument, Error>({
    queryKey: ['tokenusage', 'period', accountKey, year, month],
    queryFn: async () => (await client).getPeriod(year as number, month as number),
    enabled,
    staleTime: THIRTY_MINUTES,
    retry: retryUnlessNotFound,
  });
}

/** Periods available to query, used to populate the month/year picker. Includes a `partial` flag when the backend could not confirm the full window (see PR #96968 thread 974824). */
export function useAvailablePeriods(): UseQueryResult<AvailablePeriodsResult, Error> {
  const { client, accountKey } = useTokenUsageClient();
  return useQuery<AvailablePeriodsResult, Error>({
    queryKey: ['tokenusage', 'periods', accountKey],
    queryFn: async () => (await client).getAvailablePeriods(),
    staleTime: FIVE_MINUTES,
    retry: retryUnlessNotFound,
  });
}

/** Admin only: a specific user's usage for a period (defaults to current month). */
export function useAdminUserUsage(
  oid: string | undefined,
  year: number | undefined,
  month: number | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<TokenUsageDocument, Error> {
  const { client } = useTokenUsageClient();
  const enabled = (options?.enabled ?? true) && !!oid;
  return useQuery<TokenUsageDocument, Error>({
    // Not account-scoped: this is the target user's data, identical
    // regardless of which admin is asking, so it's safe to share the cache
    // entry across admin accounts (unlike the caller-scoped queries above).
    queryKey: ['tokenusage', 'admin-user', oid, year, month],
    queryFn: async () => (await client).getUserPeriod(oid as string, year, month),
    enabled,
    staleTime: TWO_MINUTES,
    retry: retryUnlessNotFound,
  });
}

/** Admin only: org-wide usage (including the byUser map) for a period (defaults to current month). */
export function useAdminAllUsers(
  year: number | undefined,
  month: number | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<TokenUsageDocument, Error> {
  const { client } = useTokenUsageClient();
  const enabled = options?.enabled ?? true;
  return useQuery<TokenUsageDocument, Error>({
    // Not account-scoped: org-wide data is identical for every admin.
    queryKey: ['tokenusage', 'admin-all', year, month],
    queryFn: async () => (await client).getAllUsers(year, month),
    enabled,
    staleTime: TWO_MINUTES,
    retry: retryUnlessNotFound,
  });
}
